/**
 * Montage Timeline Panel — Premiere-style video editor for Montage runs.
 *
 * Lets the user browse past Montage generations, click to add them as clips
 * on a horizontal timeline (leveraging the existing `TimelineEditor` that
 * already handles trim handles, playhead scrubbing, transitions, preview,
 * zoom and background audio). Export pipes clip state into the backend
 * `/api/recipes/timeline/export` ffmpeg renderer.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from '../utils/toast';
import {
  Film, Loader2, Plus, CheckCircle, XCircle, Play, Download,
  ChevronDown, ChevronUp, Film as FilmIcon, Upload, RotateCcw, Check
} from 'lucide-react';
import TimelineEditor from './timeline/TimelineEditor';

const API = process.env.REACT_APP_BACKEND_URL;

function withAuthToken(url, token) {
  if (!url || !token) return url;
  if (url.startsWith('http')) return url;
  const full = `${API}${url}`;
  if (!url.startsWith('/api/recipes/storage/') && !url.startsWith('/api/storage/')) return full;
  const sep = full.includes('?') ? '&' : '?';
  return `${full}${sep}token=${encodeURIComponent(token)}`;
}

const AR_OPTIONS = [
  { value: '16:9', label: '16:9 Landscape' },
  { value: '9:16', label: '9:16 Vertical' },
  { value: '1:1',  label: '1:1 Square' },
];

export default function MontageTimelinePanel({ token, runId, recipeSlug = 'montage-videos' }) {
  const [expanded, setExpanded] = useState(true);
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [shots, setShots] = useState([]);          // feeds into TimelineEditor
  const [aspect, setAspect] = useState('16:9');
  const [exportState, setExportState] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedClips, setUploadedClips] = useState([]);  // tiles in Available Clips row
  const [initialDraft, setInitialDraft] = useState(null);  // hydrates TimelineEditor on first mount
  const [saveStatus, setSaveStatus] = useState('idle');    // 'idle' | 'saving' | 'saved' | 'error'
  const fileInputRef = useRef(null);
  const hydratedRef = useRef(false);

  // Fetch draft on first expand — reconstructs the user's in-progress timeline.
  useEffect(() => {
    if (!expanded || !token || hydratedRef.current) return;
    hydratedRef.current = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/recipes/timeline/draft`, {
          params: { recipe_slug: recipeSlug },
          headers: { Authorization: `Bearer ${token}` },
        });
        const draft = data?.draft;
        if (draft && Array.isArray(draft.clips) && draft.clips.length > 0) {
          setInitialDraft({ clips: draft.clips, audio: draft.audio || null });
          if (draft.aspect_ratio) setAspect(draft.aspect_ratio);
        }
      } catch (e) {
        // Non-fatal — user just gets an empty timeline.
        console.warn('[Timeline] draft hydrate failed', e);
      }
    })();
  }, [expanded, token, recipeSlug]);

  // Commit timeline state to the backend (called debounced from TimelineEditor).
  const handleDraftChange = useCallback(async (draftPayload) => {
    if (!token) return;
    setSaveStatus('saving');
    try {
      await axios.put(`${API}/api/recipes/timeline/draft`, {
        clips: draftPayload.clips || [],
        audio: draftPayload.audio || null,
        aspect_ratio: draftPayload.aspect_ratio || aspect,
        total_duration: draftPayload.total_duration || 0,
        recipe_slug: recipeSlug,
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setSaveStatus('saved');
      // Flash "Saved" briefly, then idle so the indicator isn't visually noisy.
      setTimeout(() => setSaveStatus(prev => prev === 'saved' ? 'idle' : prev), 1800);
    } catch (e) {
      setSaveStatus('error');
    }
  }, [token, aspect, recipeSlug]);

  // Probe a video URL's duration client-side using a hidden <video> element.
  const probeDuration = (url) => new Promise((resolve) => {
    try {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      v.playsInline = true;
      const done = (d) => { try { v.remove(); } catch (_) {} resolve(d); };
      v.onloadedmetadata = () => {
        const d = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : 5;
        done(Math.min(60, Math.max(0.5, d)));
      };
      v.onerror = () => done(5);
      setTimeout(() => done(5), 8000);
      v.src = url;
    } catch (_) { resolve(5); }
  });

  const handleUploadPick = () => {
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleUploadChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';  // allow re-pick of same file
    if (!file) return;
    if (!/^video\//i.test(file.type) && !/\.(mp4|mov|m4v|webm|mkv)$/i.test(file.name)) {
      setUploadError('Please select a video file.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('File too large (max 50MB).');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/recipes/uploads`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawUrl = data.url || data.file_url;
      if (!rawUrl) throw new Error('Upload returned no URL');
      const fullUrl = withAuthToken(rawUrl, token);
      // Probe duration using the authenticated URL so CORS/auth works
      const dur = await probeDuration(fullUrl);
      // Add as a tile in the Available Clips row (same as Montage-run clips).
      // User then taps the tile to put it on the timeline.
      const uploadedClip = {
        id: `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: (file.name || 'Uploaded clip').slice(0, 28),
        video_url: fullUrl,
        duration: dur,
      };
      setUploadedClips(prev => [...prev, uploadedClip]);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Load completed clips for this editor's recipe context.
  // - Montage (default): fetch all completed Montage runs so user can drag in any clip
  // - TVC-20s: fetch ONLY the specific TVC run's 5 clips (no unrelated library noise)
  useEffect(() => {
    if (!expanded || !token) return;
    let cancel = false;
    (async () => {
      setLoadingRuns(true);
      try {
        if (recipeSlug === 'tvc-20s' && runId) {
          // Scope to THIS run only — the 5 Seedance clips become the
          // Available Clips pool, ordered by shot index.
          const { data: run } = await axios.get(
            `${API}/api/recipes/tvc-20s/runs/${runId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const tvcState = run?.tvc_state || {};
          const videos = tvcState.videos || [];
          const kfs = tvcState.videos ? (tvcState.keyframes || []) : [];
          const kfByIdx = Object.fromEntries(
            kfs.filter(k => k.url).map(k => [k.shot_idx, k.url])
          );
          // Build pseudo-"runs" that the existing Available Clips row renders
          // as tiles. One pseudo-run per OK clip; placeholders are skipped so
          // the pool only offers working clips to drop onto the timeline.
          const pseudoRuns = videos
            .filter(v => v.status === 'ok' && v.clip_url)
            .map(v => ({
              run_id: `${runId}-shot-${v.shot_idx}`,
              status: 'completed',
              inputs: { text_input: `SH${v.shot_idx + 1}` },
              metadata: { duration: v.duration || 5 },
              outputs: [
                { type: 'video', url: v.clip_url },
                ...(kfByIdx[v.shot_idx] ? [{ type: 'image', url: kfByIdx[v.shot_idx] }] : []),
              ],
            }));
          if (!cancel) setRuns(pseudoRuns);
          return;
        }
        // Default: full montage runs library
        const { data } = await axios.get(`${API}/api/recipes/montage-videos/runs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancel) return;
        const completed = (data.runs || []).filter(r => {
          const outs = r.outputs || [];
          return r.status === 'completed' && outs.some(o => o.type === 'video' && o.url);
        });
        setRuns(completed);
      } catch (e) {
        console.error('Failed to load runs', e);
      } finally {
        if (!cancel) setLoadingRuns(false);
      }
    })();
    return () => { cancel = true; };
  }, [expanded, token, recipeSlug, runId]);

  // Transform a Montage run into a "shot" object for TimelineEditor.
  // NOTE: `run.metadata.duration` stores what was *requested* at generation
  // time — after post-processing / upscaling / concat that value often no
  // longer matches the actual clip length on disk. Callers should prefer
  // `addClipFromRun` which probes the real duration client-side.
  const runToShot = useCallback((run, overrideDuration) => {
    const videoOut = (run.outputs || []).find(o => o.type === 'video' && o.url);
    const imgOut = (run.outputs || []).find(o => o.type === 'image' && o.url);
    if (!videoOut) return null;
    const videoUrlWithToken = withAuthToken(videoOut.url, token);
    const keyframeWithToken = imgOut?.url ? withAuthToken(imgOut.url, token) : null;
    const dur = (typeof overrideDuration === 'number' && overrideDuration > 0)
      ? overrideDuration
      : (Number(run?.metadata?.duration) || 5);
    return {
      id: `${run.run_id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      shot_id: (run?.inputs?.text_input_2 || run?.inputs?.text_input || 'Clip').slice(0, 28),
      video_url: videoUrlWithToken,
      keyframe_url: keyframeWithToken,
      duration: dur,
      video_status: 'completed',
      video_actual_duration: dur,
    };
  }, [token]);

  const addClipFromRun = async (run) => {
    // Probe the ACTUAL video duration before committing the clip to the
    // timeline — otherwise metadata.duration (which is "requested length" at
    // generation time, not file length) clamps every clip to its generation
    // target (often 5s) even when the real mp4 is 15s, 20s, etc.
    const videoOut = (run.outputs || []).find(o => o.type === 'video' && o.url);
    if (!videoOut) return;
    let probed = 0;
    try {
      const fullUrl = withAuthToken(videoOut.url, token);
      probed = await probeDuration(fullUrl);
    } catch (_) { /* fall through to metadata.duration */ }
    const shot = runToShot(run, probed || undefined);
    if (shot) setShots(prev => [...prev, shot]);
  };

  // Render the sophisticated TimelineEditor only once shots exist
  const timelineKey = useMemo(() => shots.length > 0 ? `tl-${shots[0].id.split('-')[0]}` : 'tl-empty', [shots]);

  // Live snapshot of clips currently on the timeline, mirrored from the
  // TimelineEditor via `onClipsChange`. Used to paint the "added" checkmark
  // on Available Clips tiles — and crucially to REMOVE that checkmark when
  // the user deletes a clip from the timeline. `shots` alone won't update
  // because TimelineEditor manages its own clips state internally.
  const [liveClips, setLiveClips] = useState([]);

  // Source URLs already represented on the timeline — used to paint a
  // "selected/added" checkmark on the corresponding tile in the Available
  // Clips rail so the user can see at a glance which clips are already in.
  //
  // Normalisation: the timeline's live `clips` store URLs with the API origin
  // prepended and a ?token=... query appended, whereas the Available Clips
  // list uses bare server paths. We key the Set by the URL's PATH portion
  // only (no origin, no query) so both match regardless of how they were
  // dressed up.
  const normaliseUrl = (u) => {
    if (!u) return '';
    try {
      // Handles absolute URLs
      const parsed = new URL(u, window.location.origin);
      return parsed.pathname;
    } catch (_) {
      // Fallback: strip any ?... suffix
      return String(u).split('?')[0];
    }
  };

  const addedSourceUrls = useMemo(() => {
    const s = new Set();
    const source = liveClips.length > 0 ? liveClips : shots;
    source.forEach(c => {
      const u = c?.videoUrl || c?.videoS3Url || c?.video_url;
      if (u) s.add(normaliseUrl(u));
    });
    return s;
  }, [liveClips, shots]);

  const isClipAdded = (rawUrl) => {
    if (!rawUrl) return false;
    return addedSourceUrls.has(normaliseUrl(rawUrl));
  };

  const handleExportPayload = async (payload) => {
    if (!payload.clips || payload.clips.length === 0) {
      alert('Add at least one clip to export.');
      return;
    }
    try {
      // Backend `/api/recipes/storage/` route validates run_id as a UUID-36 regex.
      // When the panel isn't tied to an existing recipe run we must still supply
      // a valid UUID; an ad-hoc `montage-tl-<timestamp>` string makes the
      // downstream output_url 400 with "Invalid run ID format", breaking both
      // the in-panel video preview and the Download/Open buttons.
      const fallbackRunId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now().toString(16).padStart(12, '0')}-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`;
      const body = {
        run_id: runId || fallbackRunId,
        aspect_ratio: aspect,
        clips: payload.clips,
        audio_url: payload.audio_url || undefined,
      };
      const { data } = await axios.post(`${API}/api/recipes/timeline/export`, body, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setExportState({ taskId: data.task_id, status: data.status, progress: 0 });
      pollExport(data.task_id);
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const pollExport = (taskId) => {
    const iv = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/recipes/timeline/status/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setExportState({
          taskId, status: data.status, progress: data.progress || 0,
          outputUrl: data.output_url, error: data.error,
          warning: data.warning,
        });
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(iv);
          // Surface a backend-issued warning (e.g. audio track couldn't be
          // loaded so the export is audio-less) as a visible toast, not a
          // silent UI state the user has to notice themselves.
          if (data.status === 'completed' && data.warning) {
            toast.warning(data.warning, { duration: 8000 });
          }
        }
      } catch (e) { /* transient */ }
    }, 2500);
  };

  const resetExport = () => setExportState(null);

  const downloadExport = async () => {
    if (!exportState?.outputUrl) return;
    const url = withAuthToken(exportState.outputUrl, token);
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = `timeline-export-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(blobUrl); a.remove(); }, 5000);
    } catch (e) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}
      data-testid="montage-timeline-panel"
    >
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03] transition-colors"
        data-testid="timeline-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4 text-teal-400" />
          <span className="text-xs font-semibold text-white uppercase tracking-wider">Timeline Editor</span>
          <span className="text-[10px] text-white/40">Premiere-style — stack, trim, transition, mix</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/[0.06]">

          {/* Timeline Editor — now the primary surface, sitting immediately
              below generation and above the Available Clips asset rail. The
              user's flow becomes: generate → edit on the timeline → (scroll
              down for) add more clips from past runs. */}
          {shots.length === 0 ? (
            <div
              className="rounded-lg py-10 mt-4 text-center text-xs text-white/30"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px dashed rgba(255,255,255,0.08)' }}
              data-testid="timeline-empty"
            >
              Pick a clip from below, or tap <span className="text-teal-300">+ Upload clip</span> to add one from your device.
            </div>
          ) : (
            <div
              className="rounded-xl overflow-hidden mt-4"
              style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
              data-testid="timeline-editor-container"
            >
              <TimelineEditor
                shots={shots}
                orientation={aspect}
                exportLabel="Export MP4"
                onExportPayload={handleExportPayload}
                onClipsChange={setLiveClips}
                variant="splice"
                token={token}
                apiBase={API}
                initialDraft={initialDraft}
                onDraftChange={handleDraftChange}
                saveStatus={saveStatus}
              />
            </div>
          )}

          {/* Export progress / result — stay immediately below the timeline
              so the user sees their export right where they triggered it. */}
          {exportState && exportState.status !== 'completed' && exportState.status !== 'failed' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 text-teal-400 animate-spin" />
                <span className="text-[11px] text-white/70 capitalize">{exportState.status || 'processing'}</span>
                <span className="text-[11px] text-white/40 ml-auto">{exportState.progress || 0}%</span>
              </div>
              <div className="h-1 bg-white/[0.05] rounded overflow-hidden">
                <div className="h-full bg-teal-400 transition-all" style={{ width: `${exportState.progress || 0}%` }} />
              </div>
            </div>
          )}

          {/* Available clips library — asset picker, moved BELOW the timeline
              so that the editor is the first thing a user sees after the
              Generate section. */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">
                Available Clips ({runs.length + uploadedClips.length})
              </label>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-wider text-white/40">Aspect</label>
                  <select
                    value={aspect}
                    onChange={(e) => setAspect(e.target.value)}
                    className="bg-black/40 border border-white/[0.08] rounded px-2 py-1 text-[10px] text-white/80"
                    data-testid="timeline-aspect-select"
                  >
                    {AR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {loadingRuns && <Loader2 className="w-3 h-3 text-white/40 animate-spin" />}
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
              {/* Upload-from-gallery tile — always first */}
              <button
                type="button"
                onClick={handleUploadPick}
                disabled={uploading}
                className="shrink-0 w-36 rounded-lg overflow-hidden hover:ring-2 hover:ring-teal-400/40 transition-all group disabled:opacity-50"
                style={{ background: 'rgba(20,184,166,0.08)', border: '1px dashed rgba(20,184,166,0.45)' }}
                data-testid="timeline-upload-tile"
                title="Upload video from your device"
              >
                <div className="aspect-video flex items-center justify-center relative">
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-teal-300 animate-spin" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-teal-300">
                      <Plus className="w-7 h-7" strokeWidth={1.5} />
                      <Upload className="w-3.5 h-3.5 opacity-70" />
                    </div>
                  )}
                </div>
                <div className="px-2 py-1.5 text-left">
                  <div className="text-[10px] text-teal-200/90 truncate font-semibold uppercase tracking-wider">
                    {uploading ? 'Uploading…' : 'Upload clip'}
                  </div>
                  <div className="text-[9px] text-teal-200/50">From your device</div>
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleUploadChange}
                className="hidden"
                data-testid="timeline-upload-input"
              />

              {runs.length === 0 && uploadedClips.length === 0 && !loadingRuns && (
                <div className="text-xs text-white/30 px-3 py-6 self-center">No Montage runs yet — or tap <span className="text-teal-300">+ Upload clip</span> to add your own.</div>
              )}

              {/* Uploaded clips — render right after the upload tile so users see
                  what they just uploaded. Click a tile to append it to the timeline. */}
              {uploadedClips.map(u => {
                const added = isClipAdded(u.video_url);
                return (
                <button
                  key={u.id}
                  onClick={() => setShots(prev => [...prev, {
                    id: `${u.id}-${Date.now()}`,
                    shot_id: u.name,
                    video_url: u.video_url,
                    keyframe_url: null,
                    duration: u.duration,
                    video_status: 'completed',
                    video_actual_duration: u.duration,
                  }])}
                  className={`shrink-0 w-36 rounded-lg overflow-hidden transition-all group relative active:scale-95 ${
                    added ? 'ring-2 ring-emerald-400/80 shadow-lg shadow-emerald-400/20'
                          : 'hover:ring-2 hover:ring-teal-400/40'
                  }`}
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(20,184,166,0.25)' }}
                  data-testid={`uploaded-clip-${u.id}`}
                  title={added ? 'Already on timeline — tap to add another copy' : 'Add to timeline'}
                >
                  <div className="aspect-video bg-black relative">
                    <video
                      src={u.video_url}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                      muted preload="metadata" playsInline
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
                      <Plus className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-semibold uppercase tracking-wider text-teal-200" style={{ background: 'rgba(20,184,166,0.25)' }}>
                      Uploaded
                    </div>
                    {added && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg" data-testid={`uploaded-clip-added-${u.id}`}>
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  <div className="px-2 py-1.5 text-left">
                    <div className="text-[10px] text-white/80 truncate">{u.name}</div>
                    <div className="text-[9px] text-white/30">{u.duration ? `${u.duration.toFixed(1)}s` : '—'}</div>
                  </div>
                </button>
                );
              })}

              {runs.map(r => {
                const iOut = (r.outputs || []).find(o => o.type === 'image');
                const vOut = (r.outputs || []).find(o => o.type === 'video');
                const added = isClipAdded(vOut?.url);
                return (
                  <button
                    key={r.run_id}
                    onClick={() => addClipFromRun(r)}
                    className={`shrink-0 w-36 rounded-lg overflow-hidden transition-all group relative active:scale-95 ${
                      added ? 'ring-2 ring-emerald-400/80 shadow-lg shadow-emerald-400/20'
                            : 'hover:ring-2 hover:ring-teal-400/40'
                    }`}
                    style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}
                    data-testid={`add-clip-${r.run_id.slice(0, 8)}`}
                    title={added ? 'Already on timeline — tap to add another copy' : 'Add to timeline'}
                  >
                    <div className="aspect-video bg-black relative">
                      {iOut?.url ? (
                        <img
                          src={withAuthToken(iOut.url, token)}
                          alt=""
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                          referrerPolicy="no-referrer"
                        />
                      ) : vOut?.url ? (
                        <video
                          src={withAuthToken(vOut.url, token)}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                          muted
                          preload="metadata"
                          playsInline
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full"><FilmIcon className="w-6 h-6 text-white/20" /></div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
                        <Plus className="w-6 h-6 text-white" />
                      </div>
                      {added && (
                        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg" data-testid={`run-clip-added-${r.run_id.slice(0, 8)}`}>
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                    <div className="px-2 py-1.5 text-left">
                      <div className="text-[10px] text-white/80 truncate">{(r.inputs?.text_input_2 || r.inputs?.text_input || 'Clip').slice(0, 28)}</div>
                      <div className="text-[9px] text-white/30">{r.metadata?.duration || '—'}s</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Upload error */}
          {uploadError && (
            <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="timeline-upload-error">
              {uploadError}
            </div>
          )}

          {exportState?.status === 'completed' && exportState.outputUrl && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                <span className="text-[11px] text-green-400 font-semibold">Export complete</span>
              </div>
              <video
                src={`${withAuthToken(exportState.outputUrl, token)}#t=0.1`}
                controls
                playsInline
                preload="auto"
                className="w-full rounded-lg bg-black"
                data-testid="timeline-export-preview"
              />
              <div className="flex gap-2">
                <button
                  onClick={downloadExport}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #14B8A6, #0F766E)' }}
                  data-testid="timeline-download-btn"
                >
                  <Download className="w-3.5 h-3.5" /> Download MP4
                </button>
                <a
                  href={withAuthToken(exportState.outputUrl, token)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 rounded-lg text-xs text-white/70 hover:text-white flex items-center gap-1.5"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="timeline-open-btn"
                >
                  <Play className="w-3 h-3" /> Open
                </a>
                <button
                  onClick={resetExport}
                  className="px-3 py-2 rounded-lg text-xs text-white/60 hover:text-white"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="timeline-new-export-btn"
                >
                  New
                </button>
              </div>
            </div>
          )}

          {exportState?.status === 'failed' && (() => {
            const err = exportState.error || 'Unknown error';
            const isInterrupted = /interrupted by server restart/i.test(err);
            const accent = isInterrupted ? 'amber' : 'red';
            const retry = async () => {
              try {
                const { data } = await axios.post(
                  `${API}/api/recipes/timeline/retry/${exportState.taskId}`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                setExportState({ taskId: data.task_id, status: data.status, progress: 0 });
                pollExport(data.task_id);
              } catch (e) {
                alert('Retry failed: ' + (e.response?.data?.detail || e.message));
              }
            };
            return (
              <div className="space-y-2" data-testid="timeline-export-failed">
                <div className="flex items-center gap-2">
                  <XCircle className={`w-3.5 h-3.5 ${accent === 'amber' ? 'text-amber-400' : 'text-red-400'}`} />
                  <span className={`text-[11px] font-semibold ${accent === 'amber' ? 'text-amber-300' : 'text-red-400'}`}>
                    {isInterrupted ? 'Export interrupted' : 'Export failed'}
                  </span>
                </div>
                <div
                  className="text-[10px] px-2 py-1.5 rounded"
                  style={{
                    background: isInterrupted ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)',
                    border: `1px solid ${isInterrupted ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)'}`,
                    color: isInterrupted ? 'rgba(252,211,77,0.9)' : 'rgba(252,165,165,0.9)',
                  }}
                >
                  {err}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={retry}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-2"
                    style={{ background: isInterrupted ? 'linear-gradient(135deg,#F59E0B,#B45309)' : 'linear-gradient(135deg,#14B8A6,#0F766E)' }}
                    data-testid="timeline-export-retry-btn"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Retry
                  </button>
                  <button
                    onClick={resetExport}
                    className="px-3 py-2 rounded-lg text-xs text-white/60 hover:text-white"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
